'use client'

import { createContext, type PropsWithChildren, useContext, useEffect, useState } from 'react'
import { useSegmentAnalytics } from '@nwwp/segment-analytics'
import { type ABSmartlyContextType, ABSmartlyProviderProps } from '../types'
import { abSmartlySdkConfig } from './config'
import { hydrateClientContext } from '../utils/context'
import { useDeviceType } from '../utils/deviceType/hooks'
import { getABSmartlyEventLogger } from '../utils/eventLogging'
import type { SupportedExperimentVariants } from '../utils/types'

export const ABSmartlyContext = createContext<ABSmartlyContextType | null>(null)
export const useABSmartlyContext = () => useContext(ABSmartlyContext)

export const ABSmartlyProvider = ({
  contextData,
  children,
  staticExperimentResult,
}: PropsWithChildren<ABSmartlyProviderProps> & {
  staticExperimentResult: SupportedExperimentVariants
}) => {
  const analytics = useSegmentAnalytics()

  const [smartlyContext, setSmartlyContext] = useState<ABSmartlyContextType | null>(null)
  const deviceType = useDeviceType()

  useEffect(() => {
    if (smartlyContext || !contextData) {
      return
    }

    const initContext = async () => {
      const user = await analytics.user()
      const anonymousId = user.anonymousId()

      if (!anonymousId) {
        throw new Error('No anonymous ID found for ABSmartly')
      }

      const clientSideContext = hydrateClientContext({
        sdkConfig: {
          ...abSmartlySdkConfig,
          eventLogger: getABSmartlyEventLogger({
            analytics,
            staticExperimentResult,
          }),
        },
        anonymousId,
        deviceType,
        contextData,
      })
      setSmartlyContext(clientSideContext)
    }

    initContext()
  }, [analytics, deviceType, contextData, staticExperimentResult])

  return <ABSmartlyContext.Provider value={smartlyContext}>{children}</ABSmartlyContext.Provider>
}
